import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, EnvelopeSimple } from "@phosphor-icons/react";

/**
 * 2-step "Forgot password" flow.
 * Step 1: ask for email, POST /auth/forgot-password.
 * Step 2: show code + new-password inputs, POST /auth/reset-password.
 * Server response for step 1 always returns 200 (no email enumeration).
 */
export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const sendCode = async (e) => {
    e?.preventDefault();
    if (!email.trim()) { toast.error("Enter your email"); return; }
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      toast.success("If an account exists for that email, a code has been sent");
      setStep(2);
      setCooldown(60);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send code");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    await sendCode();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (code.trim().length < 4) { toast.error("Enter the 6-digit code from your email"); return; }
    if (pw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      const res = await api.post("/auth/reset-password", { email, code: code.trim(), new_password: pw });
      if (res.data?.session_token) localStorage.setItem("session_token", res.data.session_token);
      toast.success("Password reset — you're signed in");
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="forgot-page">
      <div className="hidden lg:flex flex-col justify-between bg-zinc-900 text-white p-12 grid-bg">
        <Link to="/" className="font-display text-2xl font-bold">EXAM<span className="text-blue-400">/</span>OS</Link>
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-400 mb-4 font-semibold">Account recovery</div>
          <div className="font-display text-4xl font-bold tracking-tight leading-tight">Forgot your password? Let&rsquo;s get you back in.</div>
        </div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">Verified by email OTP</div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm">
          <Link to="/login" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-6" data-testid="forgot-back-login">
            <ArrowLeft size={12} weight="bold" /> Back to sign in
          </Link>
          {step === 1 && (
            <>
              <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-semibold">Step 1 of 2</div>
              <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Reset your password</h1>
              <p className="text-sm text-zinc-500 mb-8">Enter the email on your account and we&rsquo;ll send you a 6-digit code.</p>
              <form onSubmit={sendCode} className="space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-1.5">Email</label>
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full border border-zinc-300 px-3 py-3 text-sm focus:border-zinc-900 focus:outline-none"
                    data-testid="forgot-email-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-zinc-900 text-white py-3 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  data-testid="forgot-send-code-btn"
                >
                  {busy ? "Sending…" : <>Send code <ArrowRight size={14} weight="bold" /></>}
                </button>
              </form>
            </>
          )}
          {step === 2 && (
            <>
              <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-semibold">Step 2 of 2</div>
              <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Enter code &amp; new password</h1>
              <p className="text-sm text-zinc-500 mb-2">Sent to <span className="font-medium text-zinc-900" data-testid="forgot-email-shown">{email}</span></p>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-xs text-blue-600 hover:underline mb-4"
                data-testid="forgot-change-email"
              >Change email</button>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-1.5">Verification code</label>
                  <input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                    placeholder="123456"
                    className="w-full border border-zinc-300 px-3 py-3 text-lg tracking-[0.4em] font-mono text-center focus:border-zinc-900 focus:outline-none"
                    data-testid="forgot-code-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-1.5">New password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="At least 8 characters"
                    className="w-full border border-zinc-300 px-3 py-3 text-sm focus:border-zinc-900 focus:outline-none"
                    data-testid="forgot-new-password"
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-zinc-900 text-white py-3 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  data-testid="forgot-submit-btn"
                >
                  {busy ? "Resetting…" : <>Reset password <ArrowRight size={14} weight="bold" /></>}
                </button>
              </form>
              <div className="mt-6 text-xs text-zinc-500 flex items-center gap-2">
                <EnvelopeSimple size={14} />
                <span>Didn&apos;t receive the code?</span>
                <button
                  type="button"
                  onClick={resend}
                  disabled={cooldown > 0}
                  className="link-underline font-medium text-zinc-900 disabled:text-zinc-400 disabled:no-underline"
                  data-testid="forgot-resend-btn"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
